export const OUTFIELD_ZONES = ["left-field", "center-field", "right-field"];
export const INFIELD_ZONES = ["third-base", "shortstop", "up-the-middle", "second-base", "first-base"];

export const ZONE_ABBREV = {
  "left-field": "LF",
  "center-field": "CF",
  "right-field": "RF",
  "third-base": "3B",
  "shortstop": "SS",
  "second-base": "2B",
  "first-base": "1B",
  "up-the-middle": "Middle"
};

export const normalizeZone = (z) => {
  if (!z) return z;
  const isHR = z.endsWith("-hr");
  const base = isHR ? z.replace("-hr", "") : z;
  const abbrev = ZONE_ABBREV[base] || base;
  return isHR ? `${abbrev} (HR)` : abbrev;
};

export const formatAvg = (val) => `.${((val || 0) * 1000).toFixed(0).padStart(3, "0")}`;

export const processSprayChartData = (rawData) => {
  if (!rawData) return null;
  
  const bats = rawData.bats || "-";
  const team = rawData.team_name || "-";
  const year = rawData.year || new Date().getFullYear();
  const batsUpper = bats ? bats.toUpperCase() : "-";
  const isSwitch = ["B", "BOTH", "S", "SWITCH"].includes(batsUpper);
  const batsDisplay = isSwitch ? "R (Switch)" : (bats ? bats.substring(0, 1).toUpperCase() : "-");
  const isCareer = year === "career";
  const yearDisplay = isCareer ? "Career" : year;
  const teamDisplay = isCareer ? "All Teams" : team;
  const playerInfo = `${batsDisplay} | ${yearDisplay} | ${teamDisplay}`;

  return {
    player_name: rawData.player_name || "-",
    playerInfo,
    bats,
    team_name: team,
    year,
    counts: rawData.counts || {},
  };
};

// EventType enum values (matching backend StrEnum)
const EventType = {
  UNKNOWN: "UNK",
  NO_PLAY: "NP",
  GENERIC_OUT: "OUT",
  STRIKEOUT: "SO",
  STOLEN_BASE: "SB",
  DEFENSIVE_INDIFF: "DEF_IND",
  CAUGHT_STEALING: "CS",
  PICKOFF_ERROR: "POE",
  PICKOFF: "PO",
  WILD_PITCH: "WP",
  PASSED_BALL: "PB",
  BALK: "BK",
  OTHER_ADVANCE: "ADV",
  FOUL_ERROR: "FE",
  WALK: "BB",
  INTENTIONAL_WALK: "IBB",
  HIT_BY_PITCH: "HBP",
  INTERFERENCE: "CI",
  ERROR: "E",
  FIELDERS_CHOICE: "FC",
  SINGLE: "1B",
  DOUBLE: "2B",
  TRIPLE: "3B",
  HOME_RUN: "HR",
  STRIKEOUT_PASSED_BALL: "SO_PB",
  STRIKEOUT_WILD_PITCH: "SO_WP",
};

const computeStatsFromEvents = (events, guts) => {
  if (!events?.length || !guts) return null;

  let pa = 0, ab = 0, h = 0, singles = 0, doubles = 0, triples = 0, hr = 0;
  let bb = 0, ibb = 0, hbp = 0, sf = 0;
  let wobaNumerator = 0;

  events.forEach((e) => {
    if (!e.is_pa) return;
    
    pa++;
    const eventType = e.event_type ?? EventType.UNKNOWN;

    if (eventType === EventType.SINGLE) {
      ab++;
      h++;
      singles++;
      wobaNumerator += guts.w1b || 0;
    } else if (eventType === EventType.DOUBLE) {
      ab++;
      h++;
      doubles++;
      wobaNumerator += guts.w2b || 0;
    } else if (eventType === EventType.TRIPLE) {
      ab++;
      h++;
      triples++;
      wobaNumerator += guts.w3b || 0;
    } else if (eventType === EventType.HOME_RUN) {
      ab++;
      h++;
      hr++;
      wobaNumerator += guts.whr || 0;
    } else if (eventType === EventType.WALK) {
      bb++;
      wobaNumerator += guts.wbb || 0;
    } else if (eventType === EventType.INTENTIONAL_WALK) {
      bb++;
      ibb++;
      wobaNumerator += guts.wbb || 0;
    } else if (eventType === EventType.HIT_BY_PITCH) {
      hbp++;
      wobaNumerator += guts.whbp || 0;
    } else if (eventType === EventType.STRIKEOUT || 
               eventType === EventType.STRIKEOUT_PASSED_BALL || 
               eventType === EventType.STRIKEOUT_WILD_PITCH) {
      ab++;
    } else if (eventType === EventType.GENERIC_OUT || 
               eventType === EventType.ERROR || 
               eventType === EventType.FIELDERS_CHOICE) {
      ab++;
    } else if (eventType !== EventType.STOLEN_BASE && 
               eventType !== EventType.CAUGHT_STEALING) {
      ab++;
    }

    if (e.is_sf) sf++;
  });

  const wobaDenominator = ab + bb + hbp + sf;
  const woba = wobaDenominator > 0 ? wobaNumerator / wobaDenominator : 0;
  const avg = ab > 0 ? h / ab : 0;
  const obp = (ab + bb + hbp + sf) > 0 ? (h + bb + hbp) / (ab + bb + hbp + sf) : 0;
  const slg = ab > 0 ? (singles + 2 * doubles + 3 * triples + 4 * hr) / ab : 0;

  return {
    pa,
    ab,
    h,
    singles,
    doubles,
    triples,
    hr,
    bb,
    ibb,
    hbp,
    sf,
    avg: round(avg, 3),
    obp: round(obp, 3),
    slg: round(slg, 3),
    woba: round(woba, 3),
  };
};

const round = (val, decimals) => Math.round(val * Math.pow(10, decimals)) / Math.pow(10, decimals);

export const computeAggregates = (events, options = {}) => {
  const { handFilter = { L: true, R: true }, selectedZones = [], guts = null } = options;
  
  const evts = filterEvents(events, { handFilter, selectedZones });
  
  const outCounts = { "left-field": 0, "center-field": 0, "right-field": 0 };
  const outHR = { "left-field": 0, "center-field": 0, "right-field": 0 };
  const inCounts = { "third-base": 0, "shortstop": 0, "second-base": 0, "first-base": 0, "up-the-middle": 0 };

  let bbGround = 0, bbFly = 0, bbLined = 0, bbPopped = 0;
  let dirPull = 0, dirOppo = 0, dirMiddle = 0;
  let pa = 0, hits = 0, singles = 0, doubles = 0, triples = 0, hrs = 0, bb = 0, hbp = 0, sf = 0;
  const wobas = [];

  evts.forEach((e) => {
    const z = e.field_zone;
    if (z) {
      const isHR = z.endsWith("-hr");
      const base = isHR ? z.replace("-hr", "") : z;
      if (OUTFIELD_ZONES.includes(base)) {
        isHR ? outHR[base]++ : outCounts[base]++;
      } else if (INFIELD_ZONES.includes(base)) {
        inCounts[base]++;
      }
    }

    if (e.is_ground) bbGround++;
    if (e.is_fly) bbFly++;
    if (e.is_lined) bbLined++;
    if (e.is_popped) bbPopped++;
    if (e.direction === 'pull') dirPull++;
    if (e.direction === 'oppo') dirOppo++;
    if (e.direction === 'middle') dirMiddle++;

    if (e.is_pa) {
      pa++;
      if (typeof e.woba === 'number') wobas.push(e.woba);
      if (e.is_bb) bb++;
      if (e.is_hbp) hbp++;
      if (e.is_sf) sf++;
      if (e.is_single) { hits++; singles++; }
      else if (e.is_double) { hits++; doubles++; }
      else if (e.is_triple) { hits++; triples++; }
      else if (e.is_hr) { hits++; hrs++; }
    }
  });

  const outNonHRTotal = OUTFIELD_ZONES.reduce((s, k) => s + outCounts[k], 0);
  const outHRTotal = OUTFIELD_ZONES.reduce((s, k) => s + outHR[k], 0);
  const inTotal = INFIELD_ZONES.reduce((s, k) => s + inCounts[k], 0);
  const allBattedBalls = outNonHRTotal + outHRTotal + inTotal;

  const pct = (n) => allBattedBalls ? Math.round((n / allBattedBalls) * 100) : 0;

  const outfieldZones = OUTFIELD_ZONES.map((z) => ({
    id: z,
    percentage: pct(outCounts[z]),
    hrCount: outHR[z],
    hrPercentage: pct(outHR[z]),
  }));

  const infieldZones = INFIELD_ZONES.map((z) => ({
    id: z,
    percentage: pct(inCounts[z]),
  }));

  const bbTotal = bbGround + bbFly + bbLined + bbPopped;
  const dirTotal = dirPull + dirOppo + dirMiddle;
  const hasDirection = dirTotal > 0;
  const ab = pa - bb - hbp - sf;

  const byHand = (hand) => {
    const subset = evts.filter(e => (e.pitcher_throws || '').toUpperCase().startsWith(hand));
    const handStats = computeStatsFromEvents(subset, guts);
    if (handStats) {
      return {
        PA: handStats.pa,
        battingAvg: handStats.avg,
        onBasePercentage: handStats.obp,
        wOBA: handStats.woba,
      };
    }
    
    // Fallback to old method if no guts
    let s_pa = 0, s_hits = 0, s_bb = 0, s_hbp = 0, s_sf = 0;
    const s_w = [];
    
    subset.forEach(e => {
      if (e.is_pa) {
        s_pa++;
        if (typeof e.woba === 'number') s_w.push(e.woba);
        if (e.is_bb) s_bb++;
        if (e.is_hbp) s_hbp++;
        if (e.is_sf) s_sf++;
        if (e.is_single || e.is_double || e.is_triple || e.is_hr) s_hits++;
      }
    });
    
    const s_ab = s_pa - s_bb - s_hbp - s_sf;
    return {
      PA: s_pa,
      battingAvg: s_ab > 0 ? s_hits / s_ab : 0,
      onBasePercentage: (s_ab + s_bb + s_hbp + s_sf) > 0 ? (s_hits + s_bb + s_hbp) / (s_ab + s_bb + s_hbp + s_sf) : 0,
      wOBA: s_w.length ? s_w.reduce((a, b) => a + b, 0) / s_w.length : 0,
    };
  };

  const tb = singles + 2 * doubles + 3 * triples + 4 * hrs;
  const computedStats = computeStatsFromEvents(evts, guts);
  const mainStats = computedStats ? {
    PA: computedStats.pa,
    battingAvg: computedStats.avg,
    onBasePercentage: computedStats.obp,
    slugging: computedStats.slg,
    wOBA: computedStats.woba,
  } : {
    PA: pa,
    battingAvg: ab > 0 ? hits / ab : 0,
    onBasePercentage: (ab + bb + hbp + sf) > 0 ? (hits + bb + hbp) / (ab + bb + hbp + sf) : 0,
    slugging: ab > 0 ? tb / ab : 0,
    wOBA: wobas.length ? wobas.reduce((a, b) => a + b, 0) / wobas.length : 0,
  };

  return {
    outfieldZones,
    infieldZones,
    stats: {
      ...mainStats,
      batted: {
        air: bbTotal ? 100 - Math.round((bbGround / bbTotal) * 100) : 0,
        ground: bbTotal ? Math.round((bbGround / bbTotal) * 100) : 0,
        pull: hasDirection ? Math.round((dirPull / dirTotal) * 100) : null,
        oppo: hasDirection ? Math.round((dirOppo / dirTotal) * 100) : null,
        middle: hasDirection ? Math.round((dirMiddle / dirTotal) * 100) : null,
        pullAir: dirPull > 0 ? Math.round((evts.filter(e => e.direction === 'pull' && (e.is_fly || e.is_lined || e.is_hr)).length / dirPull) * 100) : null,
        backspinGroundball: hasDirection ? Math.round((evts.filter(e => e.direction === 'oppo' && e.is_ground).length / dirTotal) * 100) : null,
      },
      vsRHP: byHand('R'),
      vsLHP: byHand('L'),
      ...(computedStats ? { computedStats } : {}),
    }
  };
};

export const filterEvents = (events, { handFilter = { L: true, R: true }, selectedZones = [] }) => {
  if (!events?.length) return [];
  
  const allowedHands = new Set([
    ...(handFilter.L ? ["L"] : []),
    ...(handFilter.R ? ["R"] : []),
  ]);
  const hrZones = new Set(selectedZones.filter(z => z.endsWith("-hr")));
  const baseZones = new Set(selectedZones.filter(z => !z.endsWith("-hr")));

  return events.filter((e) => {
    if (allowedHands.size > 0 && allowedHands.size < 2) {
      const hand = (e.pitcher_throws || "").toUpperCase().startsWith("L") ? "L" : 
                   (e.pitcher_throws || "").toUpperCase().startsWith("R") ? "R" : null;
      if (hand && !allowedHands.has(hand)) return false;
    }
    
    if (selectedZones.length > 0) {
      if (!e.field_zone) return false;
      const baseZone = e.field_zone.endsWith("-hr") ? e.field_zone.replace("-hr", "") : e.field_zone;
      const hrZone = baseZone + "-hr";
      const isHR = e.is_hr || e.field_zone.endsWith("-hr");
      
      return isHR ? hrZones.has(hrZone) : baseZones.has(baseZone);
    }
    return true;
  });
};

export const getHeatColor = (percentage, type = 'outfield') => {
  if (type === 'infield') {
    if (percentage >= 25) return "#EF4444"; // Very hot - dark red
    if (percentage >= 18) return "#F87171"; // Hot - red
    if (percentage >= 12) return "#FCA5A5"; // Above average - medium pink
    if (percentage >= 6) return "#FECACA";  // Average - light pink
    if (percentage > 0) return "#FEE2E2";   // Below average - pale pink
    return "#FFFFFF";
  }
  if (percentage >= 40) return "#EF4444";   // Very hot - dark red
  if (percentage >= 30) return "#F87171";   // Hot - red
  if (percentage >= 20) return "#FCA5A5";   // Above average - medium pink
  if (percentage >= 10) return "#FECACA";   // Average - light pink
  return "#FEE2E2";                          // Below average - pale pink
};

